import { useEffect, useState, useRef, useCallback } from "react";
import FullCalendar from "@fullcalendar/react";
import type { EventClickArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from '@fullcalendar/core/locales/es';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  TextField,
  Button,
  MenuItem,
  Chip,
  Divider,
  IconButton,
  Snackbar,
  Alert,
  Fab,
} from "@mui/material";
import { updateAppointment, getAppoinmentByUser } from "../../services/appointmentService";
import { useAuth } from "../../context/AuthContext";
import { getCalendarEvents } from "../../services/calendarService";
import CreateAppointmentModal from "./CreateAppFromCalendar";
import { AccessTime, Home, CalendarMonth } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';
import AddIcon from '@mui/icons-material/Add';
import Swal from 'sweetalert2';


interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  description?: string;
  origin: "internal" | "google";
  status?: string;
  property?: string;
  agent?: string;
  notes?: string;
  role?: "client" | "realtor"
}

interface Props {
  events: CalendarEvent[];
  setEvents: (events: CalendarEvent[]) => void;
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  selectedEvent: CalendarEvent | null;
  setSelectedEvent: (event: CalendarEvent | null) => void;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
}

const CalendarWithAppointments: React.FC<Props> = ({
  events,
  setEvents,
  drawerOpen,
  selectedEvent,
  setSelectedEvent,
  modalOpen,
  setModalOpen
}) => {
  type AppointmentStatus = "Scheduled" | "confirmed" | "Cancelled";
  const { userToken, googleAccessToken, isBroker } = useAuth();
  const calendarRef = useRef<FullCalendar | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createDateTime, setCreateDateTime] = useState<Date | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [formData, setFormData] = useState<{
    appointmentDate: string;
    appointmentTime: string;
    status: AppointmentStatus;
    notes: string;
  }>({
    appointmentDate: "",
    appointmentTime: "",
    status: "Scheduled",
    notes: "",
  });

  const currentRealtor = userToken?.user?.profiles.find(p => p.roleId === 3);
  const currentClient = userToken?.user?.profiles.find(p => p.roleId === 7);

  useEffect(() => {
    if (calendarRef.current) {
      calendarRef.current.getApi().updateSize();
    }
  }, [drawerOpen]);;

  const fetchAllEvents = useCallback(async () => {
    if (!userToken) {
      console.warn("No hay userToken");
      return;
    }

    try {
      let mappedClientEvents: CalendarEvent[] = [];

      const userAppointments = await getAppoinmentByUser(currentClient?.id ?? 0, currentRealtor?.id ?? 0);

      if (userAppointments) {
        mappedClientEvents = userAppointments.map((appt) => ({
          id: appt.appointmentId.toString(),
          title: `${appt.propertyName} con ${appt.realtorUsername}`,
          start: `${appt.appointmentDate.split("T")[0]}T${appt.appointmentTime}`,
          status: appt.status,
          description: `Agente: ${appt.realtorUsername}`,
          property: appt.propertyName,
          agent: appt.realtorUsername,
          notes: appt.notes,
          origin: "internal",
          role: "client",
        }));
      }

      let mappedRealtorEvents: CalendarEvent[] = [];

      let mappedGoogleEvents: CalendarEvent[] = [];
      if (googleAccessToken) {
        try {
          const googleResponse = await getCalendarEvents(googleAccessToken);
          mappedGoogleEvents = googleResponse.items.map((event) => ({
            id: event.id!,
            title: event.summary,
            start: event.start.dateTime,
            description: event.description || "",
            origin: "google",
          }));
        } catch (err) {
          console.error("❌ Error al traer eventos de Google:", err);
        }
      }

      const allEvents = [
        ...mappedClientEvents,
        ...mappedRealtorEvents,
        ...mappedGoogleEvents,
      ];

      const upcomingEvents = allEvents.filter(
        (event) => event.status?.toLowerCase() !== "completed"
      );

      setEvents(upcomingEvents as CalendarEvent[]);
    } catch (error) {
      console.error("❌ Error al traer citas:", error);
    }
  }, [userToken, googleAccessToken, setEvents, currentClient, currentRealtor]);



  useEffect(() => {
    fetchAllEvents();
  }, [fetchAllEvents]);


  useEffect(() => {
    const fetchGoogleCalendarEvents = async () => {
      if (!googleAccessToken) {
        console.warn("No hay token de acceso de Google");
        return;
      }

      try {
        const calendarData = await getCalendarEvents(googleAccessToken);
        console.log("📅 Eventos de Google Calendar:", calendarData.items);
      } catch (err) {
        console.error("❌ Error al obtener eventos del calendario de Google:", err);
      }
    };

    fetchGoogleCalendarEvents();
  }, [googleAccessToken]);

  useEffect(() => {
    if (selectedEvent && selectedEvent.origin === "internal") {
      const [date, time] = selectedEvent.start.split("T");
      setFormData({
        appointmentDate: date,
        appointmentTime: time.slice(0, 5),
        status: (selectedEvent.status as AppointmentStatus) || "Scheduled",
        notes: selectedEvent.notes || "",
      });
    }
  }, [selectedEvent]);

  const handleUpdate = async () => {
    if (!selectedEvent) return;

    const dto = {
      appointmentId: parseInt(selectedEvent.id),
      appointmentDate: formData.appointmentDate,
      appointmentTime: formData.appointmentTime,
      status: formData.status,
      notes: formData.notes,
    };

    const result = await updateAppointment(dto);
    if (result) {
      setModalOpen(false);
      setEditMode(false);
      fetchAllEvents();
      Swal.fire({
        title: "Cita actualizada correctamente",
        text: "Revisa tu calendario para ver la cita agendada.",
        icon: "success",
        confirmButtonText: "Aceptar",
      });
    }
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const event = events.find((e) => e.id === clickInfo.event.id);
    if (event) {
      setSelectedEvent(event);
      setModalOpen(true);
    }
  };

  const renderEventDetails = () => {
    if (!selectedEvent) return null;

    const isInternal = selectedEvent.origin === "internal";

    return (
      <Dialog
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditMode(false);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: { xs: 1, sm: 2 },
            bgcolor: '#fefefe',
            mx: { xs: 1, sm: 3 },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {editMode ? '¡Edita tu cita libremente!' : selectedEvent.title}
          </Box>
          <IconButton
            onClick={() => {
              setModalOpen(false);
              setEditMode(false);
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers>
          {isInternal ? (
            editMode ? (
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="Fecha"
                  type="date"
                  value={formData.appointmentDate}
                  onChange={(e) => setFormData({ ...formData, appointmentDate: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Hora"
                  type="time"
                  value={formData.appointmentTime}
                  onChange={(e) => setFormData({ ...formData, appointmentTime: e.target.value })}
                  fullWidth
                />
                <TextField
                  select
                  label="Estado"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as AppointmentStatus })}
                  fullWidth
                >

                  <MenuItem value="Scheduled">Programada</MenuItem>
                  <MenuItem value="confirmed">Confirmada</MenuItem>
                  <MenuItem value="Cancelled">Cancelada</MenuItem>

                </TextField>
                <TextField
                  label="Notas"
                  multiline
                  minRows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  fullWidth
                />
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button sx={{
                    mt: 2,
                    background: "linear-gradient(45deg, #f75151ff, #a32323ff)",
                    boxShadow: "0 4px 20px rgba(188, 24, 24, 0.3)",
                  }} variant="contained" onClick={() => setEditMode(false)}>Cancelar</Button>
                  <Button sx={{
                    mt: 2,
                    background: "linear-gradient(45deg, #4caf50, #2e7d32)",
                    boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)",
                  }} onClick={handleUpdate} variant="contained" color="primary">
                    Guardar cambios
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mb: 0.5,
                }}>
                  <Chip
                    label={spanishStatus(selectedEvent.status)}
                    color={getStatusColor(selectedEvent.status || '')}
                    size="small"
                  />
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <CalendarMonth fontSize="small" sx={{ color: '#4caf50' }} />
                  <Typography>Fecha: {selectedEvent.start?.split("T")[0]}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <AccessTime fontSize="small" sx={{ color: '#2196f3' }} />
                  <Typography>Hora: {selectedEvent.start?.split("T")[1]}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Home fontSize="small" sx={{ color: '#000000ff' }} />
                  <Typography>Propiedad: {selectedEvent.property}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonPinCircleIcon fontSize="small" sx={{ color: '#ff9800' }} />
                  <Typography fontWeight={500}>Agente:</Typography>
                  <Typography>{selectedEvent.agent}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <InfoOutlinedIcon fontSize="small" sx={{ color: '#9c27b0' }} />
                  <Typography fontWeight={500}>Rol:</Typography>
                  <Typography>
                    {selectedEvent.role === "realtor" ? "Agendada por mí (Broker)" : "Cita como cliente"}
                  </Typography>
                </Box>
                {selectedEvent.notes && (
                  <Box mt={2} px={2} py={1} bgcolor="#f1f1f1" borderRadius={2}>
                    <Typography fontSize="0.9rem" color="text.secondary">
                      {selectedEvent.notes}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 2 }} />
                <Button
                  variant="contained" fullWidth sx={{
                    mt: 2,
                    background: "linear-gradient(45deg, #4caf50, #2e7d32)",
                    boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)",
                  }}
                  onClick={() => setEditMode(true)}>
                  Editar cita
                </Button>
              </Box>
            )
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarMonth fontSize="small" sx={{ color: '#4caf50' }} />
                <Typography>
                  Inicio: {new Date(selectedEvent.start).toLocaleString()}
                </Typography>
              </Box>
              <Typography>
                Descripción: {selectedEvent.description || 'Sin descripción'}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const getStatusColor = (status: string): "success" | "error" | "warning" | "info" | "primary" | "secondary" => {
    switch (status.toLowerCase()) {
      case "pending":
        return "warning";
      case "scheduled":
        return "success";
      case "cancelled":
        return "error";
      default:
        return "info";
    }
  };

  const spanishStatus = (status: string | undefined): string => {
    switch (status?.toLowerCase()) {
      case 'scheduled':
        return 'Programada';
      case 'confirmed':
        return 'Confirmada';
      case 'cancelled':
        return 'Cancelada';
      case 'pending':
        return 'Pendiente';
      default:
        return status || '';
    }
  };

  if (!events.length) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="h6">No hay citas programadas</Typography>
      </Box>
    );
  }


  return (
    <>
      <Box
        sx={{
          overflowX: 'auto',
          width: '99%',
          ml: '2px',
          margin: '0px',
          px: '2',
          backgroundColor: '#fffffffc',
          borderRadius: 2,
          p: { xs: 1, sm: 2, md: 3 },
          minHeight: '80vh',
          transition: 'all 0.3s ease',
          '& .fc': {
            fontFamily: 'Roboto, sans-serif',
            color: '#202124',
            fontSize: {
              xs: '0.75rem',
              sm: '0.85rem',
              md: '1rem',
            },
          },
          '& .fc-toolbar': {
            borderBottom: '1px solid #e0e0e0',
            paddingBottom: 1,
            mb: 2,
            display: 'flex',
            flexDirection: 'row', // ✅ Siempre en línea
            flexWrap: 'wrap', // ✅ Permite envolver si el espacio es muy pequeño
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          },
          '& .fc-toolbar-chunk': {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.5rem',
          },

          '& .fc-toolbar-title': {
            fontSize: {
              xs: '1rem',
              sm: '1.2rem',
              md: '1.4rem',
            },
            fontWeight: 'bold',
            textAlign: 'center',
            minWidth: 'fit-content',
          },
          '& .fc-button': {
            backgroundColor: '#4caf50',
            color: 'white',
            borderRadius: '6px',
            textTransform: 'capitalize',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: '#388e3c',
            },
            fontSize: { xs: '0.65rem', sm: '0.8rem' },
            px: { xs: 1, sm: 2 },
            py: { xs: 0.5, sm: 1 },
          },

          '& .fc-button-group': {
            ml: '0px',
            gap: '1px',
          },
          '& .fc-event': {
            border: 'none',
            fontWeight: 500,
            cursor: 'pointer',
            borderRadius: '10px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
            lineHeight: 1.4,
            '&:hover': {
              backgroundColor: '#66bb6a',
            },
            padding: '4px 6px',
            fontSize: { xs: '0.6rem', sm: '0.75rem', md: '0.85rem' },
          },
          '& .fc-daygrid-day': {
            backgroundColor: '#ffffff',
            height: '100px !important',
            padding: '4px',
          },
          '& .fc-daygrid-day-frame': {
            minHeight: '80px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
          },

          '& .fc-scrollgrid-sync-table': {
            tableLayout: 'fixed',
          },

          '& .fc-timegrid-slot': {
            height: '1.5em !important',
          },

          '& .fc-timegrid-event': {
            margin: '4px 2px',
            padding: '2px 4px',
            fontSize: '0.75rem',
          },

          '& .fc-timegrid-col': {
            padding: '0 4px',
          },
        }}
      >
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            start: 'prev,next today',
            center: 'title',
            end: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          locale={esLocale}
          events={events}
          eventClick={handleEventClick}
          height="auto"
          dateClick={(info) => {
            const clickedDate = new Date(info.dateStr);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (clickedDate < today) {

              return;
            }

            if (isBroker) {
              setCreateModalOpen(true);
              setCreateDateTime(info.date);
            }
          }}
        />
        {isBroker && (
          <Fab
            color="success"
            aria-label="Agregar cita"
            onClick={() => {
              setCreateDateTime(null);
              setCreateModalOpen(true);
            }}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1500,
              boxShadow: 4,
              '@media (max-width: 600px)': {
                bottom: 16,
                right: 16,
              },
            }}
          >
            <AddIcon />
          </Fab>
        )}
        <CreateAppointmentModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          defaultDateTime={createDateTime ? createDateTime.toISOString() : undefined}
          realtorId={currentRealtor?.id ?? 0}
          onAppointmentCreated={() => {
            fetchAllEvents();
            setCreateModalOpen(false);
            setSnackbarOpen(true);
          }}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity="success" variant="filled">
            ¡Cita creada con éxito!
          </Alert>
        </Snackbar>
        {renderEventDetails()}

      </Box >
    </>

  );
};

export default CalendarWithAppointments;